import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  TrendingUp,
  ChevronRight,
  Sparkles,
  FileText,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { categoryIcons } from "../lib/constants";
import { useApi } from "../hooks/useApi";
import { DashboardLayout } from "../components/DashboardLayout";

export const ValuationPage = () => {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [valuating, setValuating] = useState(null);
  const [valuation, setValuation] = useState(null);

  const loadItems = useCallback(async () => {
    try {
      const res = await api.get("/data");
      setItems(res.data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleValuate = async (itemId) => {
    setValuating(itemId);
    try {
      const res = await api.post("/valuation", { data_item_id: itemId });
      setValuation(res.data);
      toast.success("Valuation complete!");
      loadItems();
    } catch (e) {
      toast.error("Failed to valuate");
    } finally {
      setValuating(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="valuation-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">AI Data Valuation</h1>
          <p className="text-gray-400 mt-1">Get AI-powered insights on your data&apos;s market value</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Data Items */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-4">Select Data to Valuate</h2>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
              </div>
            ) : items.length === 0 ? (
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 text-center border border-purple-500/20">
                <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Add data to your vault first to get valuations</p>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item) => {
                  const Icon = categoryIcons[item.category] || FileText;
                  return (
                    <div
                      key={item.id}
                      className="bg-white/5 backdrop-blur-sm rounded-xl p-4 flex items-center justify-between border border-purple-500/20"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center border border-purple-500/30">
                          <Icon className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{item.name}</p>
                          <p className="text-sm text-gray-400">
                            Current: ${item.estimated_value?.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleValuate(item.id)}
                        disabled={valuating === item.id}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-cyan-500 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-purple-500/30 transition disabled:opacity-50"
                        data-testid={`valuate-${item.id}`}
                      >
                        {valuating === item.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                        Valuate
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Valuation Result */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-4">Valuation Results</h2>
            {valuation ? (
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20" data-testid="valuation-result">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Estimated Value</p>
                    <p className="text-3xl font-bold text-white">${valuation.estimated_value?.toFixed(2)}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-xl border border-purple-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-purple-400" />
                      <span className="font-medium text-gray-300">Market Demand</span>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        valuation.market_demand === "Very High"
                          ? "bg-green-500/20 text-green-400"
                          : valuation.market_demand === "High"
                          ? "bg-cyan-500/20 text-cyan-400"
                          : "bg-gray-500/20 text-gray-400"
                      }`}
                    >
                      {valuation.market_demand}
                    </span>
                  </div>

                  <div className="p-4 bg-white/5 rounded-xl border border-purple-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-cyan-400" />
                      <span className="font-medium text-gray-300">AI Insights</span>
                    </div>
                    <p className="text-gray-400 text-sm">{valuation.ai_insights}</p>
                  </div>

                  <div className="p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle2 className="w-4 h-4 text-yellow-400" />
                      <span className="font-medium text-gray-300">Recommendations</span>
                    </div>
                    <ul className="space-y-2">
                      {valuation.recommendations?.map((rec) => (
                        <li key={rec} className="flex items-start gap-2 text-sm text-gray-400">
                          <ChevronRight className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-12 text-center border border-purple-500/20">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-4 border border-purple-500/30">
                  <Sparkles className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-300 mb-2">No Valuation Yet</h3>
                <p className="text-gray-500">Select a data item and click &quot;Valuate&quot; to get AI-powered insights</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// Marketplace Page
